import React, { FunctionComponent, useState } from "react";
import styles from "./styles.module.scss";
import {
  Box,
  Card,
  DialogActions,
  DialogContent,
  Stack,
  ToggleButton,
  ToggleButtonGroup,
  Toolbar,
  useTheme
} from "@mui/material";
import { strings } from "../../../../localization/LocalizedStrings";
import { TextField } from "../../../TextField";
import { FormikProvider, useFormik } from "formik";
import * as Yup from "yup";
import { AlertDialog } from "../../../AlertDialog";
import { StatusCodesHelper } from "../../../../models/StatusCodesHelper";
import { FieldTitle } from "../../../Text/FieldTitle";
import { ContainedButton } from "../../../ContainedButton";
import { ToolbarButton } from "../../../toolbar/ToolbarButton";
import { useIsMobile } from "../../../../hooks/isMobile";
import { FreeTextListItem } from "../../../FreeTextListItem";
import { ModifyFreeTextAttributeDialog } from "../../ModifyFreeTextAttributeDialog";
import { Firebase } from "../../../../services/Firebase";
import { PiperDialog } from "../../../PiperDialog";
import { IconType } from "../../../../icons";
import { IEquipment } from "../../../../hooks/equipments";
import { useAddEquipment } from "../../../../hooks/equipments/useAddEquipment";
import { useModifyEquipment } from "../../../../hooks/equipments/useModifyEquipment";
import { useDeleteEquipment } from "../../../../hooks/equipments/useDeleteEquipment";
import { GearItemsList } from "../../gearItems/GearItemsList";
import { AttributeDialog } from "../../AttributeDialog";
import { GearItemsDesktop } from "../../gearItems/GearItemsDesktop";

export const ModifyEquipmentDialog: FunctionComponent<IModifyEquipmentDialogProps> = ({
  open,
  onClose,
  equipment,
  onNewItem,
  onModifiedItem,
  onDeletedItem
}) => {
  const theme = useTheme();
  const isMobile = useIsMobile();
  const dialogTitle = equipment ? strings.equipment_title : strings.new_equipment_title;
  const { addEquipment } = useAddEquipment();
  const { modifyEquipment } = useModifyEquipment();
  const { deleteEquipment } = useDeleteEquipment();
  const [isLoading, setIsLoading] = useState(false);
  const [isModifyProfileDialogOpen, setIsModifyProfileDialogOpen] = useState(false);
  const [isGearItemsDialogOpen, setIsGearItemsDialogOpen] = useState(false);
  const [alertMessage, setAlertMessage] = useState("");
  const [alertIsOpen, setAlertIsOpen] = useState(false);
  const [isConfirmCloseAlertOpen, setIsConfirmCloseAlertOpen] = useState(false);
  const tryToClose = () => {
    if (isLoading) return;
    if (formik.dirty) {
      setIsConfirmCloseAlertOpen(true);
    } else {
      onClose();
    }
  };
  const validationSchema = Yup.object({
    name: Yup.string().required(strings.field_missing)
  });
  const formik = useFormik({
    initialValues: {
      name: equipment?.name ?? "",
      description: equipment?.description ?? "",
      equipmentStatus: equipment?.status ?? "active",
      gearItems: equipment?.gearItems ?? []
    },
    validateOnBlur: false,
    validateOnChange: false,
    enableReinitialize: true,
    onSubmit: async ({ name, description, equipmentStatus, gearItems }) => {
      setIsLoading(true);
      const orderedGearItems = gearItems.map((gearItem, index) => ({
        ...gearItem,
        order: index
      }));
      if (equipment) {
        const { status, body } = await modifyEquipment({
          uuid: equipment.uuid,
          name: name,
          description: description,
          status: equipmentStatus,
          gearItems: orderedGearItems
        });
        if (StatusCodesHelper.isSuccessful(status)) {
          Firebase.shared.logFirebaseEvent("attribute_store_action", { isNew: false });
          onModifiedItem?.(body);
          onClose();
        } else {
          setAlertMessage(body.message);
          setAlertIsOpen(true);
        }
      } else {
        const { status, body } = await addEquipment({
          name: name,
          description: description,
          status: equipmentStatus,
          gearItems: orderedGearItems
        });
        if (StatusCodesHelper.isSuccessful(status)) {
          Firebase.shared.logFirebaseEvent("attribute_equipment_action", { isNew: true });
          onNewItem?.(body);
          onClose();
        } else {
          setAlertMessage(body.message);
          setAlertIsOpen(true);
        }
      }
      setIsLoading(false);
    },
    validationSchema: validationSchema
  });
  const onDelete = async () => {
    if (!equipment) return;
    setIsLoading(true);
    const { status, body } = await deleteEquipment({ uuid: equipment.uuid });
    if (StatusCodesHelper.isSuccessful(status)) {
      onDeletedItem?.(equipment);
      onClose();
    } else {
      setAlertMessage(body.message);
      setAlertIsOpen(true);
    }
    setIsLoading(false);
  };
  return (
    <FormikProvider value={formik}>
      <PiperDialog fullWidth maxWidth={"md"} open={open} onClose={tryToClose}>
        <Toolbar>
          <h3>{dialogTitle}</h3>
          <Box sx={{ flexGrow: 1 }} />
          {equipment && (
            <ToolbarButton
              onClick={onDelete}
              tooltip={strings.delete}
              iconType={IconType.DeleteForever}
              color={theme.palette.colors?.remove}
            />
          )}
          <ToolbarButton
            loading={isLoading}
            onClick={tryToClose}
            tooltip={strings.close}
            iconType={IconType.Close}
          />
        </Toolbar>
        <DialogContent sx={{ paddingTop: 0, paddingBottom: 0 }}>
          <Stack direction={isMobile ? "column" : "row"} spacing={4}>
            <div style={{ width: isMobile ? "auto" : "50%" }}>
              <FieldTitle>{strings.equipment_name_title}</FieldTitle>
              <div className={styles.textField}>
                <TextField
                  id="name"
                  name="name"
                  size="small"
                  autoFocus={!isMobile}
                  placeholder={strings.equipment_name_placeholder}
                  value={formik.values.name}
                  onChange={formik.handleChange}
                  enterAction={formik.submitForm}
                />
              </div>
              <FieldTitle>{strings.description_title}</FieldTitle>
              <Card className={styles.profileCard}>
                <FreeTextListItem
                  hideTitle
                  detail={formik.values.description ?? "--"}
                  maxLines={4}
                  onClick={() => setIsModifyProfileDialogOpen(true)}
                />
              </Card>
              <FieldTitle>{strings.status_title}</FieldTitle>
              <ToggleButtonGroup
                className={styles.statusToggle}
                value={formik.values.equipmentStatus}
                exclusive
                onChange={(_event, value) => {
                  if (value !== null) {
                    formik.setFieldValue("equipmentStatus", value).then();
                  }
                }}
                sx={{ height: "30px", width: "100%" }}
              >
                <ToggleButton
                  className={styles.toggleButton}
                  sx={{ color: theme.palette.colors?.disabled }}
                  value={"active"}
                >
                  {strings.status_active}
                </ToggleButton>
                <ToggleButton
                  className={styles.toggleButton}
                  sx={{ color: theme.palette.colors?.disabled }}
                  value={"inactive"}
                >
                  {strings.status_inactive}
                </ToggleButton>
              </ToggleButtonGroup>
            </div>
            <div style={{ width: isMobile ? "auto" : "50%" }}>
              <Stack direction={"row"} alignItems={"center"}>
                <FieldTitle>{strings.gear_items_title}</FieldTitle>
                <Box flexGrow={1} />
                <ToolbarButton
                  tooltip={strings.add_gear_item}
                  iconType={IconType.LibraryAdd}
                  onClick={() => setIsGearItemsDialogOpen(true)}
                />
              </Stack>
              <GearItemsList
                className={styles.gearItemsList}
                gearItems={formik.values.gearItems}
                onDelete={gearItem =>
                  formik.setFieldValue(
                    "gearItems",
                    formik.values.gearItems.filter(g => g.gearItemId !== gearItem.uuid)
                  )
                }
              />
            </div>
          </Stack>
        </DialogContent>
        <DialogActions>
          <ContainedButton type={"submit"} onClick={formik.handleSubmit}>
            {strings.alert_confirm}
          </ContainedButton>
        </DialogActions>
      </PiperDialog>
      <ModifyFreeTextAttributeDialog
        title={strings.description_title}
        description={strings.edit_description_description}
        placeholder={strings.description_title}
        value={formik.values.description}
        open={isModifyProfileDialogOpen}
        onClose={() => setIsModifyProfileDialogOpen(false)}
        onChange={newValue => formik.setFieldValue("description", newValue)}
        onClearValue={() => formik.setFieldValue("description", null)}
      />
      <AttributeDialog open={isGearItemsDialogOpen} onClose={() => setIsGearItemsDialogOpen(false)}>
        <GearItemsDesktop
          handleClose={() => setIsGearItemsDialogOpen(false)}
          columnWidth={isMobile ? 12 : 6}
          preselectedIds={formik.values.gearItems.map(
            equipmentGearItem => equipmentGearItem.gearItemId
          )}
          onGearItemSelected={gearItem => {
            setIsGearItemsDialogOpen(false);
            if (
              !formik.values.gearItems
                .map(equipmentGearItem => equipmentGearItem.gearItemId)
                .includes(gearItem.uuid)
            ) {
              formik
                .setFieldValue("gearItems", [
                  ...formik.values.gearItems,
                  {
                    equipmentId: equipment?.uuid,
                    gearItemId: gearItem.uuid,
                    gearItem: gearItem,
                    order: formik.values.gearItems.length
                  }
                ])
                .then();
            }
          }}
        />
      </AttributeDialog>
      <AlertDialog
        message={strings.alert_cancel_confirmation}
        open={isConfirmCloseAlertOpen}
        setOpen={setIsConfirmCloseAlertOpen}
        isConfirm={true}
        onConfirm={onClose}
        confirmTitle={strings.yes}
        cancelTitle={strings.no}
      />
      <AlertDialog message={alertMessage} open={alertIsOpen} setOpen={setAlertIsOpen} />
    </FormikProvider>
  );
};

export interface IModifyEquipmentDialogProps {
  open: boolean;
  onClose: () => void;
  equipment?: IEquipment;
  onNewItem?: (item: IEquipment) => void;
  onModifiedItem?: (item: IEquipment) => void;
  onDeletedItem?: (item: IEquipment) => void;
}
